import * as React from 'react';

import type { Weight } from '@rematter/frontend-lib-api-client';
import { Weight as WeightIcon } from 'lucide-react';
import {
  NumberField as AriaNumberField,
  NumberFieldProps as AriaNumberFieldProps,
  ValidationResult as AriaValidationResult,
  composeRenderProps,
  Key,
  Text,
} from 'react-aria-components';

import { cn } from '../../../lib/stylingUtils';
import {
  Unit,
  UnitInfo,
  DEFAULT_WEIGHT_UNITS,
  convertToPounds,
  convertFromPounds,
  createWeight,
  parseWeight,
} from '../../../lib/weights';
import { Button } from '../buttons/Button';
import { FieldError, FieldGroup, Label } from '../Field';
import { Select } from '../pickers/Select';

import { NumberFieldInput } from './NumberField';

/**
 * Interface for UnitComboBox component props
 */
interface UnitComboBoxProps {
  /** List of available weight units to display in the dropdown */
  weightUnits?: UnitInfo[];
  /** Currently selected weight unit */
  selectedUnit?: Unit;
  /** Callback fired when the unit selection changes */
  onUnitChange?: (oldUnit?: Unit, newUnit?: Unit) => void;
  /** Whether the combobox is disabled */
  isDisabled?: boolean;
  /** Additional CSS class names */
  className?: string;
}

/**
 * WeightField component props interface.
 * Extends AriaNumberFieldProps but replaces onChange with weight-specific handlers.
 * Supports both legacy number values (in pounds) and new Weight objects.
 */
export interface WeightFieldProps extends Omit<
  AriaNumberFieldProps,
  'onChange' | 'value'
> {
  /** Label text displayed above the input */
  label?: string;
  /** Description text displayed below the input */
  description?: string;
  /** Error message shown when validation fails */
  errorMessage?: string | ((validation: AriaValidationResult) => string);
  /** The currently selected weight unit for display */
  defaultUnit: Unit;
  /** Whether the take weight button should be shown. */
  withTakeWeight?: boolean;

  // Legacy support for backward compatibility
  /** @deprecated Use weightValue instead. Legacy weight value in pounds */
  value?: number;
  /** @deprecated Use onWeightValueChange instead. Callback when the weight value changes (returns value in pounds) */
  onWeightChange?: (weightInPounds: number) => void;

  // New Weight object support
  /** Weight object with value and unit */
  weightValue?: Weight | null;
  /** Callback when the weight value changes (returns Weight object) */
  onWeightValueChange?: (weight: Weight | null) => void;

  /** Callback when the weight unit changes */
  onUnitChange?: (unit?: Unit) => void;
  /** Callback when the weight capture button is pressed */
  onTakeWeightPressed?: () => void;
  /** Whether the weight unit can be changed */
  unitLocked?: boolean;
  /** List of available weight units */
  weightUnits?: UnitInfo[];
}

/**
 * UnitComboBox component.
 *
 * A dropdown selector for weight units (pounds, kilograms, etc.) with symbols and names.
 *
 * @param props - UnitComboBox properties
 * @returns Rendered UnitComboBox component
 */
const UnitComboBox = ({
  weightUnits = DEFAULT_WEIGHT_UNITS,
  selectedUnit,
  isDisabled,
  onUnitChange,
  className,
}: UnitComboBoxProps) => {
  const selectedItem = React.useMemo(
    () =>
      weightUnits.find((item) => item.unit === selectedUnit) ?? weightUnits[0],
    [selectedUnit, weightUnits],
  );

  /**
   * Handles selection changes in the weight unit dropdown
   *
   * @param key - The key of the selected weight unit
   */
  const handleSelectionChange = (newKey: Key | null | undefined) => {
    if (newKey && onUnitChange) {
      onUnitChange(selectedItem.unit, newKey as Unit);
    }
  };

  const options = React.useMemo(
    () =>
      weightUnits.map((weightUnitInfo) => ({
        key: weightUnitInfo.unit,
        label: (
          <div className="flex items-center gap-2">
            <span className="font-medium">{weightUnitInfo.symbol}</span>
            <span>{weightUnitInfo.name}</span>
          </div>
        ),
        selectedLabel: weightUnitInfo.symbol,
      })),
    [weightUnits],
  );

  return (
    <Select
      className={cn('@UnitComboBox', 'w-24', className)}
      value={selectedUnit}
      onChange={handleSelectionChange}
      isDisabled={isDisabled}
      options={options}
      showArrow={true}
    />
  );
};

/**
 * WeightField component.
 *
 * A specialized number input field for weight values with unit selection.
 * The component handles automatic unit conversion while maintaining the internal
 * value in pounds. Includes a button to capture weight from external sources
 * and a dropdown to select different weight units.
 *
 * @example
 * ```tsx
 * <WeightField
 *   label="Patient Weight"
 *   description="Enter the patient's weight"
 *   weightInPounds={150}
 *   weightUnit="lb"
 *   onWeightChange={handleWeightChange}
 *   onUnitChange={handleUnitChange}
 * />
 * ```
 *
 * @param props - WeightField properties
 * @returns Rendered WeightField component
 */
export const WeightField = ({
  label,
  description,
  errorMessage,
  className,
  // Legacy props
  value,
  onWeightChange,
  // New props
  weightValue,
  onWeightValueChange,
  // Common props
  defaultUnit,
  withTakeWeight,
  onUnitChange,
  onTakeWeightPressed,
  unitLocked = false,
  weightUnits = DEFAULT_WEIGHT_UNITS,
  ...props
}: WeightFieldProps) => {
  // Determine current weight value and unit from either legacy or new props
  const currentWeight = React.useMemo(() => {
    if (weightValue !== undefined) {
      // New Weight object approach
      return parseWeight(weightValue);
    } else if (value !== undefined) {
      // Legacy number approach (in pounds)
      return { value, unit: 'pound' as Unit };
    }
    return null;
  }, [weightValue, value]);

  const [weightUnit, setUnit] = React.useState(
    () => currentWeight?.unit || defaultUnit,
  );

  // Convert display value based on selected unit
  const displayValue = React.useMemo(() => {
    if (!currentWeight) return 0;

    // If current weight is in the same unit as display unit, use directly
    if (currentWeight.unit === weightUnit) {
      return currentWeight.value;
    }

    // Otherwise convert via pounds
    const poundsValue = convertToPounds(
      currentWeight.value,
      currentWeight.unit,
    );
    return convertFromPounds(poundsValue, weightUnit);
  }, [currentWeight, weightUnit]);

  // Disable hotkeys for now
  // const ref = useHotkeys(
  //   'ctrl+space',
  //   (event) => {
  //     console.log(event);
  //     if (withTakeWeight) {
  //       event.preventDefault();
  //       onTakeWeightPressed?.();
  //     }
  //   },
  //   { enabled: withTakeWeight },
  // );

  const handleValueChange = (newValue: number | null) => {
    const finalValue = newValue ?? 0;

    // Create Weight object with current unit
    const newWeight = createWeight(finalValue, weightUnit);

    // Call new callback if provided
    onWeightValueChange?.(newWeight);

    // Call legacy callback if provided (backward compatibility)
    if (onWeightChange) {
      const valueInPounds = convertToPounds(finalValue, weightUnit);
      onWeightChange(valueInPounds);
    }
  };

  const handleUnitChange = (oldUnit?: Unit, newUnit?: Unit) => {
    if (newUnit) {
      console.log('unit changed', oldUnit, newUnit, currentWeight);
      setUnit(newUnit);
      onUnitChange?.(newUnit);

      // If we have a current value, convert it to the new unit and trigger change
      if (currentWeight) {
        const newWeight = createWeight(displayValue, newUnit);
        onWeightValueChange?.(newWeight);

        // Legacy callback support
        if (onWeightChange) {
          const valueInPounds = convertToPounds(displayValue, newUnit);
          onWeightChange(valueInPounds);
        }
      }
    }
  };

  return (
    <AriaNumberField
      className={composeRenderProps(
        cn('@WeightField', className),
        (className) => cn('group flex flex-col gap-2', className),
      )}
      // ref={ref}
      value={displayValue}
      onChange={handleValueChange}
      {...props}
    >
      {label && <Label>{label}</Label>}
      <FieldGroup className="flex items-center gap-1 pr-0">
        <NumberFieldInput className="grow" />

        {withTakeWeight && (
          <Button
            variant="text"
            className="size-6 p-1"
            size="icon"
            slot={null}
            onPress={onTakeWeightPressed}
            keyboardShortcut="Alt+Space"
            icon={<WeightIcon aria-hidden />}
          />
        )}

        <UnitComboBox
          weightUnits={weightUnits}
          selectedUnit={weightUnit}
          onUnitChange={handleUnitChange}
          isDisabled={unitLocked}
          className="min-w-20"
        />
      </FieldGroup>
      {description && (
        <Text className="text-sm text-foreground/20" slot="description">
          {description}
        </Text>
      )}
      <FieldError>{errorMessage}</FieldError>
    </AriaNumberField>
  );
};

export type { UnitInfo, Unit, Weight };
